/**
 * One frame screen-rect source (Part 06 decision 1), from models rather than
 * the DOM wherever a model exists:
 *
 * 1. focus: a frame presented here sits in its presentation box (the model,
 *    so a running enter transform never shows);
 * 2. floating: a floating window this display owns, its global box minus
 *    the window origin;
 * 3. the workspace view adapter: a wall card (or its tile, tick or index
 *    fallback) or a canvas node.
 *
 * Results are cached per animation frame and model change, so N cursors cost
 * one computation per frame.
 */
import { useCallback, useRef, useSyncExternalStore } from "react";
import type { FrameScreenRect } from "@/components/cursors/frame-cursor-geometry";
import { useFloatingFrameStore } from "@/desktop/floating-frame-store";
import { getWindowOrigin } from "@/desktop/use-window-origin";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { subscribeFrameAnchors } from "@/lib/frame-anchors";
import { currentFocusSurfaceBox } from "@/lib/focus/focus-surface";
import { presentedWindowBox } from "@/lib/focus/focus-windows";
import { isFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import { isTauri } from "@/lib/is-tauri";
import {
  subscribeWorkspaceViews,
  workspaceViewAdapter,
} from "@/lib/workspace-view-adapter";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { usePresentationStore } from "@/stores/presentation-store";

export interface FrameGlobalRect extends FrameScreenRect {
  displayId: number | null;
}

function focusRect(frameId: string): FrameScreenRect | null {
  const { presentation } = usePresentationStore.getState();
  if (!presentation || !isFocusVisibleHere()) return null;
  const { mode, compactFrameOverlayIds } = useDesktopMode.getState();
  // Over the desktop (compact, peek) only the overlay frames float.
  if (isTauri() && mode !== "denk" && !compactFrameOverlayIds.includes(frameId))
    return null;
  const box = presentedWindowBox(presentation, frameId);
  if (!box) return null;
  const surface = currentFocusSurfaceBox();
  return {
    left: surface.x + box.x,
    top: surface.y + box.y,
    width: box.width,
    height: box.height,
  };
}

function floatingRect(frameId: string): FrameScreenRect | null {
  const floating = useFloatingFrameStore.getState().frames[frameId];
  if (!floating || floating.displayId !== getWindowDisplayId()) return null;
  const origin = getWindowOrigin();
  return {
    left: floating.box.x - origin.x,
    top: floating.box.y - origin.y,
    width: floating.box.width,
    height: floating.box.height,
  };
}

function computeFrameScreenRect(frameId: string): FrameScreenRect | null {
  return (
    focusRect(frameId) ??
    floatingRect(frameId) ??
    workspaceViewAdapter().frameRect(frameId)
  );
}

/** Whether the frame shows in a window here (focus or floating), not as a card. */
export function frameOpenInWindow(frameId: string): boolean {
  return focusRect(frameId) !== null || floatingRect(frameId) !== null;
}

/** Whether the frame is a floating window on this display. */
export function frameFloatsHere(frameId: string): boolean {
  return floatingRect(frameId) !== null;
}

/* ---------- Change sources ---------- */

const listeners = new Set<() => void>();
let generation = 0;
let notifyScheduled = false;
let sourcesAttached = false;

function fireListeners(): void {
  notifyScheduled = false;
  for (const listener of [...listeners]) listener();
}

function modelChanged(): void {
  generation += 1;
  if (notifyScheduled || listeners.size === 0) return;
  notifyScheduled = true;
  requestAnimationFrame(fireListeners);
}

/** Every model a rect reads, watched for the life of the window. */
function attachSources(): void {
  if (sourcesAttached) return;
  sourcesAttached = true;
  usePresentationStore.subscribe(modelChanged);
  useFloatingFrameStore.subscribe(modelChanged);
  useDesktopMode.subscribe(modelChanged);
  subscribeFrameAnchors(modelChanged);
  subscribeWorkspaceViews(modelChanged);
  window.addEventListener("resize", modelChanged);
}

/* ---------- Per-frame cache ---------- */

let fallbackFrame = 0;
let fallbackScheduled = false;

/** The current animation frame's timestamp, the same for every reader. */
function animationFrameKey(): number {
  const time = document.timeline?.currentTime;
  if (typeof time === "number") return time;
  // Without a document timeline, one serial per animation frame.
  if (!fallbackScheduled) {
    fallbackScheduled = true;
    requestAnimationFrame(() => {
      fallbackScheduled = false;
      fallbackFrame += 1;
    });
  }
  return fallbackFrame;
}

const cache = new Map<string, FrameScreenRect | null>();
let cacheFrame: number | null = null;
let cacheGeneration = -1;

/** Frame `frameId`'s rect in this window's client pixels, or null. */
export function frameScreenRect(frameId: string): FrameScreenRect | null {
  attachSources();
  const frame = animationFrameKey();
  if (frame !== cacheFrame || generation !== cacheGeneration) {
    cache.clear();
    cacheFrame = frame;
    cacheGeneration = generation;
  }
  if (cache.has(frameId)) return cache.get(frameId) ?? null;
  const rect = computeFrameScreenRect(frameId);
  cache.set(frameId, rect);
  return rect;
}

/**
 * `frameScreenRect` in global logical pixels, for the presentation display
 * `displayId` (null: this window's). Another display's window answers null.
 */
export function frameGlobalRect(
  frameId: string,
  displayId: number | null = getWindowDisplayId(),
): FrameGlobalRect | null {
  if (displayId !== getWindowDisplayId()) return null;
  const rect = frameScreenRect(frameId);
  if (!rect) return null;
  const origin = getWindowOrigin();
  return {
    left: rect.left + origin.x,
    top: rect.top + origin.y,
    width: rect.width,
    height: rect.height,
    displayId,
  };
}

/** Fires (once per animation frame) when frame rects may have changed. */
export function subscribeFrameRects(listener: () => void): () => void {
  attachSources();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function sameRect(
  a: FrameScreenRect | null,
  b: FrameScreenRect | null,
): boolean {
  if (!a || !b) return a === b;
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
}

/** `frameScreenRect`, re-rendering when the rect changes; a null id reads null. */
export function useFrameScreenRect(
  frameId: string | null,
): FrameScreenRect | null {
  const latest = useRef<FrameScreenRect | null>(null);
  const snapshot = useCallback(() => {
    const next = frameId ? frameScreenRect(frameId) : null;
    if (!sameRect(latest.current, next)) latest.current = next;
    return latest.current;
  }, [frameId]);
  return useSyncExternalStore(subscribeFrameRects, snapshot);
}
